import React, { useState } from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { relativeTime } from '../../partner/format';
import { Card, EASE, ageDays, plural } from './primitives';

export type InboxKind = 'opportunity' | 'request' | 'quote' | 'action' | 'recommendation' | 'connection';

export interface InboxItem {
  key: string;
  kind: InboxKind;
  title: string;
  meta: string;
  since: string | null;
  onOpen: () => void;
}

/* The categorical marks of the inbox. Every one is named in the legend and on
   its row, so the colour is never the only way to tell them apart. */
export const INBOX_KIND: Record<InboxKind, { label: string; group: string; tone: string }> = {
  opportunity: { label: 'Opportunity', group: 'Decisions', tone: '#303238' },
  request: { label: 'Approval', group: 'Decisions', tone: '#E8D500' },
  quote: { label: 'Budget', group: 'Decisions', tone: '#92949D' },
  action: { label: 'Action', group: 'Actions', tone: '#666971' },
  recommendation: { label: 'Recommendation', group: 'Recommendations', tone: '#B9BBC1' },
  connection: { label: 'Connection', group: 'Connections', tone: '#FD2751' },
};

const GROUPS = ['Decisions', 'Actions', 'Recommendations', 'Connections'];

export const Inbox: React.FC<{ items: InboxItem[]; loading: boolean }> = ({ items, loading }) => {
  const reduce = useReducedMotion();
  const [group, setGroup] = useState('All');
  const sorted = [...items].sort((a, b) => ageDays(b.since) - ageDays(a.since));
  const groups = GROUPS.map((g) => ({ g, n: items.filter((i) => INBOX_KIND[i.kind].group === g).length })).filter((x) => x.n > 0);
  const shown = group === 'All' ? sorted : sorted.filter((i) => INBOX_KIND[i.kind].group === group);
  const oldest = sorted[0];
  const kinds = (Object.keys(INBOX_KIND) as InboxKind[])
    .map((k) => ({ k, n: items.filter((i) => i.kind === k).length }))
    .filter((x) => x.n > 0);

  let sub = 'Nothing is waiting on you';
  if (items.length > 0) sub = `${plural(items.length, 'item', 'items')}, oldest ${relativeTime(oldest.since).toLowerCase()}`;

  return (
    <Card id="owner-decisions" title="Needs your decision" sub={sub} bodyClassName="pt-3">
      {items.length > 0 && (
        <>
          <div className="flex h-1.5 overflow-hidden rounded-full bg-canvas-inset" role="img" aria-label={kinds.map((x) => `${INBOX_KIND[x.k].label} ${x.n}`).join(', ')}>
            {kinds.map((x) => (
              <motion.span
                key={x.k}
                className="h-full border-r-2 border-white last:border-r-0"
                style={{ background: INBOX_KIND[x.k].tone }}
                initial={reduce ? false : { width: 0 }}
                animate={{ width: `${(x.n / items.length) * 100}%` }}
                transition={{ duration: 0.7, ease: EASE }}
              />
            ))}
          </div>
          <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1">
            {kinds.map((x) => (
              <span key={x.k} className="inline-flex items-center gap-1.5 text-[11px] text-ink-muted">
                <i className="h-2 w-2 rounded-[2px]" style={{ background: INBOX_KIND[x.k].tone }} />
                {INBOX_KIND[x.k].label} <b className="font-semibold tabular-nums text-ink">{x.n}</b>
              </span>
            ))}
          </div>
          {groups.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-1 rounded-lg bg-canvas-inset p-1" role="tablist" aria-label="Filter what is waiting">
              {[{ g: 'All', n: items.length }, ...groups].map((x) => (
                <button
                  key={x.g}
                  type="button"
                  role="tab"
                  aria-selected={group === x.g}
                  onClick={() => setGroup(x.g)}
                  className={clsx('relative flex-1 whitespace-nowrap rounded-md px-2 py-1 text-[11.5px] font-semibold transition-colors duration-150', group === x.g && 'text-ink', group !== x.g && 'text-ink-dim hover:text-ink')}
                >
                  {group === x.g && <motion.span layoutId="inbox-tab" className="absolute inset-0 rounded-md bg-white shadow-[0_1px_2px_rgba(24,25,29,0.08)]" transition={{ duration: 0.22, ease: EASE }} />}
                  <span className="relative">{x.g} <span className="tabular-nums text-ink-soft">{x.n}</span></span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {loading && items.length === 0 && (
        <div className="mt-2 grid gap-2" aria-busy="true">
          {[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-canvas-inset" />)}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="grid place-items-center rounded-xl bg-canvas px-4 py-7 text-center">
          <div>
            <CheckCircle2 size={20} className="mx-auto text-ok-dim" aria-hidden />
            <p className="m-0 mt-2 text-[13px] font-semibold text-ink">All clear</p>
            <p className="m-0 mt-0.5 text-[12px] text-ink-dim">Decisions, actions and recommendations appear here.</p>
          </div>
        </div>
      )}

      {items.length > 0 && (
        <ul className="-mx-2 mt-3 max-h-[232px] min-h-0 list-none overflow-y-auto p-0 pb-2 [mask-image:linear-gradient(to_bottom,black_calc(100%-18px),transparent)]">
          <AnimatePresence initial={false}>
            {shown.map((item, i) => {
              const look = INBOX_KIND[item.kind];
              const old = ageDays(item.since) >= 30;
              return (
                <motion.li
                  key={item.key}
                  layout={!reduce}
                  initial={reduce ? false : { opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduce ? undefined : { opacity: 0, transition: { duration: 0.12 } }}
                  transition={{ duration: 0.25, delay: Math.min(i, 8) * 0.025, ease: EASE }}
                >
                  <button
                    type="button"
                    onClick={item.onOpen}
                    className="group grid w-full grid-cols-[3px_minmax(0,1fr)_auto] items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors duration-150 hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
                  >
                    <span className="h-8 rounded-full" style={{ background: look.tone }} aria-hidden />
                    <span className="min-w-0">
                      <b className="block truncate text-[13px] font-semibold text-ink" title={item.title}>{item.title}</b>
                      <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11.5px] text-ink-dim">
                        <span className="shrink-0 font-semibold text-ink-muted">{look.label}</span>
                        <span className="truncate">· {item.meta}</span>
                      </span>
                    </span>
                    <span className="flex flex-col items-end gap-1">
                      <span className={clsx('whitespace-nowrap text-[11px] tabular-nums', old && 'font-semibold text-ink', !old && 'text-ink-soft')}>{relativeTime(item.since)}</span>
                      <ArrowRight size={13} className="text-ink-soft transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-ink" aria-hidden />
                    </span>
                  </button>
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ul>
      )}
    </Card>
  );
};
